'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  usePaginatedQuery as useCachedPaginatedQuery,
  useQueries as useCachedQueries,
  useQuery as useCachedQuery,
} from 'convex-helpers/react/cache';
import {
  getFunctionName,
  makeFunctionReference,
  type FunctionArgs,
  type FunctionReference,
  type FunctionReturnType,
} from 'convex/server';
import type { Value } from 'convex/values';
import type {
  OptionalRestArgsOrSkip,
  PaginatedQueryArgs,
  PaginatedQueryReference,
  UsePaginatedQueryReturnType,
} from 'convex/react';
import {
  isPaginatedSeedFor,
  isQuerySeedFor,
  serverSeedKey,
  type ServerQuerySeeds,
} from './keys';
import { useIsRenderIdentity } from '@/providers/render-identity';
import { useQueryWithStatus } from '@/lib/convex';

/**
 * First results the server fetched for this request (same token, same args),
 * served until each live subscription delivers once — Convex's preloaded-query
 * semantics, applied through drop-in replacements for the cached hooks so
 * deep components need no prop threading.
 */
const ServerQuerySeedsContext = createContext<ServerQuerySeeds | null>(null);

export function ServerQuerySeedsProvider({
  seeds,
  identity,
  children,
}: {
  seeds: ServerQuerySeeds | null;
  /** The user the server read `seeds` for; they apply only while it renders. */
  identity: string | null;
  children: ReactNode;
}) {
  const parent = useContext(ServerQuerySeedsContext);
  const own = useIsRenderIdentity(identity) ? seeds : null;
  // Stable while the inputs are, so seeded results keep their identity.
  const value = useMemo(
    () => (parent && own ? { ...parent, ...own } : (own ?? parent)),
    [parent, own]
  );
  return (
    <ServerQuerySeedsContext.Provider value={value}>
      {children}
    </ServerQuerySeedsContext.Provider>
  );
}

/** This tree renders from server seeds (server-rendered first paint). */
export function useHasServerQuerySeeds(): boolean {
  return useContext(ServerQuerySeedsContext) !== null;
}

/** Seed keys whose live subscription has delivered at least once. */
function useArrivedSeedKeys(
  arrivedNow: readonly string[]
): ReadonlySet<string> {
  const [arrivedKeys, setArrivedKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // Seed keys are JSON-escaped, so a newline never occurs inside one.
  const newlyArrived = arrivedNow
    .filter((key) => !arrivedKeys.has(key))
    .join('\n');
  useEffect(() => {
    if (!newlyArrived) return;
    setArrivedKeys((current) => {
      const next = new Set(current);
      for (const key of newlyArrived.split('\n')) next.add(key);
      return next;
    });
  }, [newlyArrived]);
  return arrivedKeys;
}

function useLiveArrival(key: string | null, arrived: boolean): boolean {
  const arrivedKeys = useArrivedSeedKeys(key && arrived ? [key] : []);
  return key !== null && arrivedKeys.has(key);
}

export function useQuery<Query extends FunctionReference<'query'>>(
  query: Query,
  ...queryArgs: OptionalRestArgsOrSkip<Query>
): FunctionReturnType<Query> | undefined {
  const live = useCachedQuery(query, ...queryArgs);
  const seeds = useContext(ServerQuerySeedsContext);
  const args = queryArgs[0];
  const key =
    args === 'skip' ? null : serverSeedKey('query', query, args ?? {});
  const liveHasArrived = useLiveArrival(key, live !== undefined);
  if (live !== undefined || !key || liveHasArrived) return live;
  const seed = seeds?.[key];
  return isQuerySeedFor(seed, query) ? seed.data : undefined;
}

export function usePaginatedQuery<Query extends PaginatedQueryReference>(
  query: Query,
  args: PaginatedQueryArgs<Query> | 'skip',
  options: { initialNumItems: number }
): UsePaginatedQueryReturnType<Query> {
  const live = useCachedPaginatedQuery(query, args, options);
  const seeds = useContext(ServerQuerySeedsContext);
  const key = args === 'skip' ? null : serverSeedKey('paginated', query, args);
  const liveHasArrived = useLiveArrival(
    key,
    live.status !== 'LoadingFirstPage'
  );
  if (!key || liveHasArrived || live.status !== 'LoadingFirstPage') return live;
  const seed = seeds?.[key];
  if (!isPaginatedSeedFor(seed, query)) return live;
  return seed.isDone
    ? { ...live, results: seed.page, status: 'Exhausted', isLoading: false }
    : { ...live, results: seed.page, status: 'CanLoadMore', isLoading: false };
}

/**
 * `useQueryWithStatus` served from its server seed until the live
 * subscription delivers once (or errors), for surfaces that show their own
 * error state yet should render seeded data in the first paint.
 */
export function useSeededQueryWithStatus<
  Query extends FunctionReference<'query'>,
>(
  query: Query,
  args: FunctionArgs<Query> | 'skip'
): ReturnType<typeof useQueryWithStatus<Query>> {
  const live = useQueryWithStatus(query, args);
  const seeds = useContext(ServerQuerySeedsContext);
  const key = args === 'skip' ? null : serverSeedKey('query', query, args);
  const settled = live.data !== undefined || live.isError;
  const liveHasArrived = useLiveArrival(key, settled);
  if (settled || !key || liveHasArrived) return live;
  const seed = seeds?.[key];
  return isQuerySeedFor(seed, query)
    ? {
        data: seed.data,
        error: null,
        isError: false,
        isPending: false,
        isSuccess: true,
        status: 'success',
      }
    : live;
}

/**
 * The server seed for exactly these args, without subscribing. For initial
 * state that must match the server render; live data still comes from the
 * seeded `useQuery`.
 */
export function useServerQuerySeed<Query extends FunctionReference<'query'>>(
  query: Query,
  ...queryArgs: OptionalRestArgsOrSkip<Query>
): FunctionReturnType<Query> | undefined {
  const seeds = useContext(ServerQuerySeedsContext);
  const args = queryArgs[0];
  if (!seeds || args === 'skip') return undefined;
  const seed = seeds[serverSeedKey('query', query, args ?? {})];
  return isQuerySeedFor(seed, query) ? seed.data : undefined;
}

/**
 * One subscription per args entry (a dynamic count, e.g. path batches), each
 * served from its own seed until that entry's live result arrives once. The
 * returned array keeps its identity while nothing changes, so callers can
 * compare it during render.
 */
export function useQueryBatch<Query extends FunctionReference<'query'>>(
  query: Query,
  argsList: ReadonlyArray<FunctionArgs<Query> & Record<string, Value>>
): Array<FunctionReturnType<Query> | undefined> {
  // `api.*` references are fresh proxies on every property access, so memos
  // key on the function name, never on the reference object.
  const functionName = getFunctionName(query);
  const stableQuery = useMemo(
    () => makeFunctionReference<'query'>(functionName),
    [functionName]
  );
  const requests = useMemo(
    () =>
      Object.fromEntries(
        argsList.map((args, index) => [
          String(index),
          { query: stableQuery, args },
        ])
      ),
    [argsList, stableQuery]
  );
  const live = useCachedQueries(requests);
  const seeds = useContext(ServerQuerySeedsContext);
  const keys = useMemo(
    () => argsList.map((args) => serverSeedKey('query', stableQuery, args)),
    [argsList, stableQuery]
  );
  const arrivedKeys = useArrivedSeedKeys(
    keys.filter((_, index) => live[String(index)] !== undefined)
  );
  return useMemo(
    () =>
      keys.map((key, index): FunctionReturnType<Query> | undefined => {
        const result = live[String(index)];
        if (result instanceof Error) throw result;
        if (result !== undefined || arrivedKeys.has(key)) return result;
        const seed = seeds?.[key];
        return seed?.kind === 'query' && seed.functionName === functionName
          ? seed.data
          : undefined;
      }),
    [arrivedKeys, functionName, keys, live, seeds]
  );
}
